import type { ImageProps } from "next/image";
import { stegaClean } from "next-sanity";
import type { SanityImageCrop, SanityImageDimensions } from "../sanity.types";

export function hasImageCrop(crop?: SanityImageCrop) {
  return [crop?.top, crop?.bottom, crop?.left, crop?.right].some(
    (value) => (value ?? 0) > 0,
  );
}

export function shouldPreserveSvg(
  extension: string | undefined,
  crop?: SanityImageCrop,
) {
  // Sanity only applies SVG crops when rasterizing; preview strings may contain stega metadata.
  return stegaClean(extension) === "svg" && !hasImageCrop(crop);
}

export function getSanityImageDimensions({
  assetDimensions,
  crop,
  aspectRatio,
  width,
  height,
  fill,
}: {
  assetDimensions?: SanityImageDimensions;
  crop?: SanityImageCrop;
  aspectRatio?: number;
} & Pick<Partial<ImageProps>, "width" | "height" | "fill">) {
  if (fill) return { width: undefined, height: undefined, fill: true };
  if (width && height)
    return { width: Number(width), height: Number(height), fill: false };

  const sourceWidth = assetDimensions?.width ?? 1;
  const sourceHeight = assetDimensions?.height ?? 1;
  // Match the pixel rounding used by @sanity/image-url for the crop rectangle.
  const croppedWidth = Math.max(
    1,
    Math.round(
      sourceWidth -
        (crop?.right ?? 0) * sourceWidth -
        Math.round((crop?.left ?? 0) * sourceWidth),
    ),
  );
  const croppedHeight = Math.max(
    1,
    Math.round(
      sourceHeight -
        (crop?.bottom ?? 0) * sourceHeight -
        Math.round((crop?.top ?? 0) * sourceHeight),
    ),
  );
  const ratio =
    aspectRatio ??
    (hasImageCrop(crop)
      ? croppedWidth / croppedHeight
      : (assetDimensions?.aspectRatio ?? 1));

  if (height)
    return {
      height: Number(height),
      width: Math.round(Number(height) * ratio),
      fill: false,
    };
  if (width)
    return {
      width: Number(width),
      height: Math.round(Number(width) / ratio),
      fill: false,
    };
  return { width: croppedWidth, height: croppedHeight, fill: false };
}
